/* import __COLOCATED_TEMPLATE__ from './seller-profile.hbs'; */
import { A } from '@ember/array';
import EmberObject, { computed } from '@ember/object';
import { Promise } from 'rsvp';
import { not, sort, equal, readOnly, filter } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import {
  PROFILE_SUB_SECTION_STATES,
  PROFILE_SUB_SECTION_TITLE_TO_STATE_MAP,
  PROFILE_SUB_SECTION_TITLES,
  PROFILE_PROJECTDIGEST_INCLUDES,
  PROJECTDIGEST_PAGE_SIZE,
  PROFILE_USER_INCLUDES_BUYER,
  PROFILE_USER_INCLUDES_SELLER,
} from '../../constants/components/profile';
import LightboxOverlay from 'sh-www/components/modals/overlays/lightbox';
import {
  CLASSIFICATION_CATEGORIES,
  CLASSIFICATION_TYPES,
} from 'sh-www/models/classification';
import { showModal } from "sh-www/utils/modal-controls";
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { task, waitForProperty } from 'ember-concurrency';

const CLASS_NAME = 'SH-c-seller-profile';

export default Component.extend({
  className: CLASS_NAME,

  /** HTML Component properties */
  tagName: '',

  /** Export for use in template */
  PROFILE_SUB_SECTION_STATES,

  /** Register services */
  applicationCommandService: service('commands/application-commands'),
  classificationCommandService: service('commands/classification-commands'),
  companyCommandService: service('commands/company-commands'),
  noteCommandService: service('commands/note-commands'),
  projectDigestCommandService: service('commands/projectdigest-commands'),
  userCommandService: service('commands/user-commands'),
  applicationStateManager: service('state-managers/application'),
  errorTracker: service('error-tracker'),
  session: service(),
  store: service(),
  shData: service(),

  currentUser: readOnly('session.currentUser'),

  allClassifications: [],

  allServices: [],

  /** @param {User} user */
  user: null,

  pagination: null,

  /** @param {String|null} */
  featuredDigestID: null,

  closeOverlay: () => {},

  /** @param {Function} */
  onProjectParamUpdate: () => {},

  /** @param {Boolean} */
  navigateToBlocked: false,

  /** @param {Function} */
  onSetProfileRef: () => {},

  userNotes: null,

  /** @type {Boolean} Whether there's anything that should prevent transitioning. */
  canTransitionAway: not('hasActiveNotesEditing'),

  init() {
    this._super(...arguments);
    this.set('projects', A([]));
    this.set('userNotes', A([]));

    if (this.navigateToBlocked) {
      this.updateProfileSubSection(PROFILE_SUB_SECTION_STATES.SUB_SECTION_YOUR_INFO);
    }

    this.loadProjectsWaiter.perform();
    this.triggerEmbedModal.perform();
    this.loadTopUserReviews.perform();
    this.loadAllServices.perform();
    if (this.isViewingSelf) {
      this.loadAllClassifications.perform();
    }
  },

  /** Performs any final checks or tasks before the component is destroyed. */
  cleanup() {
    if (this.get('canTransitionAway')) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      showModal(this, 'confirmation', {
        titleText: 'Do you want to leave?',
        bodyText: 'Your note will be lost if you leave now.',
        isShowCancelButton: true,
        confirmAction: () => {
          this.clearAllNotesEditing();
          resolve();
        },
      });
    });
  },

  /** @type {Boolean} Whether the notes are currently loading. */
  areNotesLoading: false,

  /** @type {Boolean} Whether a new note is being added. */
  isAddingNewNote: false,

  /** @type {Boolean} Whether a new note is being saved to the DB. */
  isSavingNewNote: false,

  /** @type {Ember.Object} Map indicating whether each note is being edited, keyed by note ID. */
  isEditingNoteMap: EmberObject.create({}),

  /** @type {Ember.Object>} Map indicating whether each note is being saved, keyed by note ID. */
  isSavingEditedNoteMap: EmberObject.create({}),

  /** @type {String<PROFILE_SUB_SECTION_STATES.*>} The state of the sub nav in the profile. */
  profileSubSectionView: PROFILE_SUB_SECTION_STATES.SUB_SECTION_PROFILE,

  /** @type {function(Object): Promise<any>} */
  updateReview: null,

  /** @type {Boolean} Whether there are existing notes being edited (i.e. not a NEW note). */
  areAnyExistingNotesBeingEdited: computed('isEditingNoteMap', function () {
    const editingNoteMap = this.get('isEditingNoteMap');
    return Object.keys(editingNoteMap).find((noteID) => editingNoteMap.get(noteID));
  }),

  /** @type {Boolean} Whether there are any (NEW and EXISTING) notes being edited. */
  hasActiveNotesEditing: computed(
    'isAddingNewNote',
    'areAnyExistingNotesBeingEdited',
    function () {
      return this.get('isAddingNewNote') || this.get('areAnyExistingNotesBeingEdited');
    },
  ),

  /** @type {Boolean} Whether the current user is looking at their own profile. */
  isViewingSelf: computed('currentUser.id', 'user.id', function () {
    return this.get('currentUser.id') === this.get('user.id');
  }),

  /** @type {Boolean} Whether the current user can edit a project on a seller profile. */
  hasProfileProjectEditAbility: computed('isViewingSelf', 'currentUser.isAdmin', function () {
    return this.isViewingSelf || this.currentUser?.isAdmin;
  }),

  /** @type {Array<String>} Sorting options for the profile notes. */
  profileNotesSortedOptions: ['updatedAt:desc'],

  /** @type {Ember.A<Note>} All the notes for a company and freelancer profile sorted. */
  profileNotesSorted: sort('userNotes', 'profileNotesSortedOptions'),

  /** @type {number} */
  featuredDigestIndex: computed('featuredDigestID', 'projects.@each.id', function() {
    const featuredDigestID = this.featuredDigestID;

    if (!featuredDigestID) {
      return -1;
    }

    return this.projects.findIndex((digest) => digest.id === featuredDigestID);
  }),

  /** @inheritDoc */
  didInsertElement() {
    this._super(...arguments);

    this.get('onSetProfileRef')(this);

    this._boundOnBeforeUnlnoad = this._handleOnBeforeUnload.bind(this);
    window.addEventListener('beforeunload', this._boundOnBeforeUnlnoad);
  },

  /** @inheritDoc */
  willDestroy() {
    this._super(...arguments);

    this.get('onSetProfileRef')(null);

    window.removeEventListener('beforeunload', this._boundOnBeforeUnlnoad);
  },

  /** @type {Boolean} Whether to show tabs on the profile. */
  _canViewTabs: computed('session.isAuthenticated', 'isViewingSelf', function () {
    return this.get('session.isAuthenticated') && !this.get('isViewingSelf');
  }),

  /**
   * Handles the case when the user is trying to leave the page.
   *
   * @return {String}
   */
  _handleOnBeforeUnload() {
    if (this.get('hasActiveNotesEditing')) {
      return 'Are you sure you want to leave? Your notes will be lost.';
    }
  },

  /** @type {Boolean} Whether the sub-nav is in the profile view. */
  _isProfileView: equal(
    'profileSubSectionView',
    PROFILE_SUB_SECTION_STATES.SUB_SECTION_PROFILE,
  ),

  /**
   * The array of objects representing the sub nav.
   *
   * @see lsg-nav-with-counts
   */
  _profileNavItems: computed('profileSubSectionView', function () {
    const navItems = A();
    const currentSubSectionState = this.get('profileSubSectionView');
    const isNavActive = (stateToCheck) => currentSubSectionState === stateToCheck;

    navItems.pushObject({
      title: PROFILE_SUB_SECTION_TITLES.PROFILE,
      isActive: () => isNavActive(PROFILE_SUB_SECTION_STATES.SUB_SECTION_PROFILE),
    });

    if (this.currentUser?.isBuyer) {
      navItems.pushObject({
        title: PROFILE_SUB_SECTION_TITLES.YOUR_INFO,
        isActive: () => isNavActive(PROFILE_SUB_SECTION_STATES.SUB_SECTION_YOUR_INFO),
        materialIcon: 'lock',
      });
    }

    if (this.currentUser?.isAdmin) {
      navItems.pushObject({
        title: PROFILE_SUB_SECTION_TITLES.ADMIN,
        isActive: () => isNavActive(PROFILE_SUB_SECTION_STATES.SUB_SECTION_ADMIN),
        materialIcon: 'lock',
      });
    }

    return navItems;
  }),

  /** Clears the state of any active notes being edited, including NEW and EXISTING notes. */
  clearAllNotesEditing() {
    this.set('isAddingNewNote', false);

    const editingNoteMap = this.get('isEditingNoteMap');
    Object.keys(editingNoteMap).forEach((noteId) => editingNoteMap.set(noteId, false));
  },

  /**
   * Loads the block user events for this freelancer.
   *
   * @return {Ember.RSVP.Promise}
   */
  loadBlockUserEvents() {
    const seller = this.get('user');

    if (seller.get('blockUserEvents.length')) {
      return Promise.resolve(true);
    }

    return this.get('companyCommandService')
      .loadBlockUserEventsForSeller(seller, 'user,blocked-by-user')
      .catch((error) => {
        const message = `loadBlockUserEvents failed - profile id ${seller.get('id')}`;
        this.get('errorTracker').error(message, this, error);
        throw error;
      });
  },

  /**
   * Loads all profile notes for a user.
   *
   * @return {Ember.RSVP.Promise}
   */
  loadProfileNotes() {
    this.set('areNotesLoading', true);

    return this.noteCommandService
      .loadNotesOnSubject(this.user, this.currentUser, 'application,created-by-user,created-about-user')
      .then((results) => {
        this.set('userNotes', results);
      })
      .finally(() => this.set('areNotesLoading', false));
  },

  /**
   * Toggles the user's profile optOut state.
   *
   * @param {Boolean} isOptOut Whether the user wants to opt out of public profiles.
   */
  toggleSellerProfileOptOut(isOptOut) {
    const profile = this.get('store').peekRecord('profile', this.get('user.profile.id'));
    profile.set('publicOptOut', isOptOut);
  },

  /**
   * Updates the sub-nav that the user is looking at.
   *
   * @param {String<PROFILE_SUB_SECTION_STATES.*>} newSubSection The section to switch to.
   * @return {Ember.RSVP.Promise} Promise for when all of data for tabs has resolved.
   */
  updateProfileSubSection(newSubSection) {
    if (newSubSection === this.get('profileSubSectionView')) {
      return Promise.resolve(true);
    }

    this.set('profileSubSectionView', newSubSection);

    if (newSubSection === PROFILE_SUB_SECTION_STATES.SUB_SECTION_YOUR_INFO) {
      return this.loadBlockUserEvents();
    }

    if (newSubSection === PROFILE_SUB_SECTION_STATES.SUB_SECTION_ADMIN) {
      return this.loadProfileNotes();
    }

    return Promise.resolve(true);
  },

  actions: {
    /**
     * Called when the user clicks on the sub-nav.
     *
     * @param {String<PROFILE_SUB_SECTION_TITLES.*>} subSectionTitle
     */
    onProfileNavItemClicked(subSectionTitle) {
      this.updateProfileSubSection(PROFILE_SUB_SECTION_TITLE_TO_STATE_MAP[subSectionTitle]);
    },

    /**
     * Called when the user clicks on the sub-nav.
     *
     * @param {String<PROFILE_SUB_SECTION_TITLES.*>} subSectionTitle
     */
    onUpdateProfileSubSection(subSectionTitle) {
      return this.updateProfileSubSection(subSectionTitle);
    },

    /**
     * Shows the featured digest in the carousel video modal.
     *
     * @param {Projectdigest} projectDigest
     */
    onShowFeaturedDigestInModal(projectDigest) {
      this.onProjectParamUpdate(projectDigest.id);
    },

    /**
     * Toggles the add a new note edit experience.
     *
     * @param {Boolean} isAddingNewNote
     */
    onToggleAddNewNote(isAddingNewNote) {
      if (isAddingNewNote) {
        this.clearAllNotesEditing();
      }

      this.set('isAddingNewNote', isAddingNewNote);
    },

    /**
     * Saves a new note on this profile.
     *
     * @param {String} noteContent
     * @return {Ember.RSVP.Promise}
     */
    onSaveNewNote(noteContent) {
      if (this.get('isSavingNewNote')) {
        return Promise.resolve(true);
      }

      this.set('isSavingNewNote', true);

      return this.get('noteCommandService')
        .saveNoteOnSubject(this.user, this.currentUser, noteContent)
        .then(() => {
          return this.loadProfileNotes().then(() => {
            return this.set('isAddingNewNote', false);
          });
        })
        .catch((error) => {
          const message = `onSaveNewNote failed profile id ${this.get('user.id')}`;
          this.get('errorTracker').error(message, this, error);
          throw error;
        })
        .finally(() => {
          this.set('isSavingNewNote', false);
        });
    },

    /**
     * Sets the edit state of an EXISTING note.
     *
     * @param {String} noteId
     * @param {Boolean} isEditing
     */
    onToggleEditExistingNote(noteId, isEditing) {
      if (isEditing) {
        this.clearAllNotesEditing();
      }

      this.get('isEditingNoteMap').set(noteId, isEditing);
    },

    /**
     * Updates the note with new content and update our state.
     *
     * @param {User} seller
     * @param {Note} note
     * @param {String} newNoteContent
     */
    onUpdateNote(note, newNoteContent) {
      const objectIsSavingKey = `isSavingEditedNoteMap.${note.get('id')}`;

      this.set(objectIsSavingKey, true);

      return this.get('noteCommandService')
        .updateNote(note, newNoteContent)
        .then(() => {
          return this.loadProfileNotes();
        })
        .then(() => {
          this.set(objectIsSavingKey, false);
        })
        .catch((error) => {
          const message = `onUpdateNote failed note id ${note.get('id')}`;
          this.get('errorTracker').error(message, this, error);
          throw error;
        })
        .finally(() => {
          this.set(objectIsSavingKey, false);
        });
    },

    /**
     * Deletes a note for a given freelancer.
     *
     * @param {Note} note
     * @return {Ember.RSVP.Promise}
     */
    onDeleteNote(note) {
      return this.get('noteCommandService').deleteNote(
        note
      ).then(() => {
        return this.loadProfileNotes();
      }).catch((error) => {
        const message = `onDeleteNote failed note id ${note.get('id')}`;
        this.get('errorTracker').error(message, this, error);
        throw error;
      });
    },
  },

  cancelProjectLoadingTask: task(function *() {
    yield this.loadProjectsWaiter.cancelAll();
    yield this.loadProjects.cancelAll();
  }),

  onSellerAddProjectDigestTask: task(function *() {
    yield this.cancelProjectLoadingTask.perform();

    this.setProperties({
      pagination: null,
      projects: A([]),
    });

    this.loadProjectsWaiter.perform();
  }),

  onSellerRemoveProjectDigestTask: task(function *(projectDigest) {
    if (!projectDigest.application) {
      yield this.cancelProjectLoadingTask.perform();

      if (this.pagination.currentPage < this.pagination.totalPages) {
        this.set('pagination', null);

        const idx = this.projects.indexOf(projectDigest);
        const page = Math.ceil((idx + 1) / PROJECTDIGEST_PAGE_SIZE);
        const removeAtStart = (page-1)*PROJECTDIGEST_PAGE_SIZE;

        this.projects.removeAt(removeAtStart, this.projects.length - removeAtStart);
        this.loadProjectsWaiter.perform(page);
      } else {
        this.projects.removeObject(projectDigest);
        this.set('pagination.total', this.pagination.total-1);
      }
    }
  }),

  updateReviewTask: task(function *(application, stars, comments, projectDigestId) {
    try{
      yield this.applicationCommandService.editReviewAdmin(application, stars, comments);
      const includes = [...PROFILE_USER_INCLUDES_BUYER, ...PROFILE_USER_INCLUDES_SELLER];
      yield this.userCommandService.getUser(this.user.id, includes);
      const projectDigest = yield this.projectDigestCommandService.getProjectDigest(projectDigestId, PROFILE_PROJECTDIGEST_INCLUDES);
      this.shData.pushResponseToStore(projectDigest);
      this.applicationStateManager.addToast('Review updated.');
    } catch {
      this.applicationStateManager.addToast('Error updating review.');
    }
  }),

  loadProjects: task(function *(pageToLoad) {
    const response = yield this.userCommandService.getProjectDigests(
      this.user.id,
      PROFILE_PROJECTDIGEST_INCLUDES,
      'priority',
      pageToLoad,
      PROJECTDIGEST_PAGE_SIZE
    );
    this.shData.pushResponseToStore(response);

    const pagination = this.shData.getPaginationModelFromResponse(response);
    const projects = this.shData.getModelArrayFromResponse(response, 'Projectdigest');
    const paginationClone = pagination.getProperties('currentPage', 'totalPages', 'total');

    this.set('pagination', paginationClone);
    this.projects.pushObjects(projects);
  }),

  triggerEmbedModal: task(function* () {
    while (true) {
      const selectedProjectDigestVideoCarouselIndex = yield waitForProperty(this, 'featuredDigestIndex', (value) => value > -1);
      const { projects } = this;

      showModal(this, LightboxOverlay, {
        allClassifications: this.allClassifications,
        allServices: this.allServices,
        selectedProjectDigestVideoCarouselIndex,
        digestsToShowForVideoCarousel: projects,
        allowSellerEdits: true,
        handleDeleteProjectdigest: this.onSellerRemoveProjectDigestTask,

        handleModalClosed: () => {
          if (!this.isDestroying) {
            this.onProjectParamUpdate(null);
          }
        },

        handleNewFeaturedDigestSelected: (newDigest) => {
          if (!this.isDestroying) {
            this.onProjectParamUpdate(newDigest.id);
          }
        },
      });

      yield waitForProperty(this, 'featuredDigestIndex', (value) => value < 0);
    }
  }),

  loadProjectsWaiter: task(function *(startPage = 1) {
    try {
      yield this.loadProjects.perform(startPage);
      while (this.pagination.currentPage < this.pagination.totalPages) {
        yield this.loadProjects.perform(this.pagination.currentPage + 1);
      }
    } catch (e) {
      this.errorTracker.error(e, this, 'There was an error loading projects.');
      this.applicationStateManager.addToast("There was an error loading user's projects. Please reload the page and try again.");
    }
  }),

  loadTopUserReviews: task(function *() {
    try {
      return yield this.userCommandService.getUserReviews(
        this.user.id,
        3,
        'top',
        'application.projectdigest,application.company,application.brand.image,reviewer'
      );
    } catch (e) {
      this.errorTracker.error(e, this, 'There was an error loading top reviews.');
      this.applicationStateManager.addToast("There was an error loading user's top reviews. Please reload the page and try again.");
    }
  }),

  loadAllClassifications: task(function *() {
    try {
      const allClassifications = yield this.classificationCommandService.loadClassifications({
        filters: {
          types: [
            CLASSIFICATION_TYPES.TYPE_DEFAULT,
          ],
          categories: [
            CLASSIFICATION_CATEGORIES.CATEGORY_SUBJECT,
            CLASSIFICATION_CATEGORIES.CATEGORY_INDUSTRY,
          ],
        },
      });

      this.set('allClassifications', allClassifications);
    } catch (e) {
      this.errorTracker.error(e, this, 'There was an error loading list of content tags.');
      this.applicationStateManager.addToast("There was an error loading the list of content tags. Please reload the page and try again.");
    }
  }),

  loadAllServices: task(function *() {
    try {
      const allServices = yield this.store.findAll('service');

      this.set('allServices', allServices);
    } catch (e) {
      this.errorTracker.error(e, this, 'There was an error loading list of services.');
      this.applicationStateManager.addToast("There was an error loading the list of services. Please reload the page and try again.");
    }
  }),

  onSaveAdminNote: task(function *(projectDigest, noteText) {
    try {
      yield this.noteCommandService.saveAdminNote(projectDigest, noteText);
    } catch (e) {
      this.errorTracker.error('Failed to save admin note', this, e);
      this.applicationStateManager.addToast("Failed to save admin's note. Please reload the page and try again.");
    }
  }),

  onUnverifyProjectDigest: task(function *(projectDigest) {
    try {
      yield this.projectDigestCommandService.setProjectDigestVerification(projectDigest, false);
      this.applicationStateManager.addToast('Project unverified');
    } catch (e) {
      this.errorTracker.error('Failed to unverify project', this, e);

      showModal(this, 'confirmation', {
        titleText: 'An error ocurred',
        bodyText: 'Please refresh and try again.',
        actionButtonText: 'Ok',
      });
    }
  }),

  onVerifyProjectDigest: task(function *(projectDigest) {
    try {
      yield this.projectDigestCommandService.setProjectDigestVerification(projectDigest, true);
      this.applicationStateManager.addToast('Project verified');
    } catch (e) {
      this.errorTracker.error('Failed to verify project', this, e);

      showModal(this, 'confirmation', {
        titleText: 'An error ocurred',
        bodyText: 'Please refresh and try again.',
        actionButtonText: 'Ok',
      });
    }
  }),

  onDeleteProjectDigest: task(function *(projectDigest, deleteReview) {
    try {
      const includes = ['review'];
      yield this.projectDigestCommandService.deleteProjectDigest(projectDigest, deleteReview, includes);
      this.applicationStateManager.addToast('Project deleted');
    } catch (e) {
      handleErrorResponseWithModal(this, e, 'Could not delete project digest');
    }
  }),

  onSetBadLink: task(function *(projectDigest) {
    try {
      yield this.projectDigestCommandService.setBadLink(projectDigest.id, true);
      const response = yield this.projectDigestCommandService.getProjectDigest(projectDigest.id, PROFILE_PROJECTDIGEST_INCLUDES);
      this.shData.pushResponseToStore(response);
    } catch(error) {
      handleErrorResponseWithModal(this, error, 'There was a problem setting bad link status. Please reload the page and try again.');
    }
  }),

  onRestoreProjectDigest: task(function *(projectDigest) {
    try {
      yield this.projectDigestCommandService.restoreProjectDigest(projectDigest);
      this.applicationStateManager.addToast('Project restored');
    } catch (e) {
      this.errorTracker.error('Failed to restore project', this, e);

      showModal(this, 'confirmation', {
        titleText: 'An error ocurred',
        bodyText: 'Please refresh and try again.',
        actionButtonText: 'Ok',
      });
    }
  }),
});
